import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAudit } from "../context/AuditContext";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import API from "../lib/api";
import {
  Target,
  ArrowRight,
  Search,
  CheckCircle2,
  Shield,
  ChevronRight,
  ListFilter,
  Eye,
} from "lucide-react";
import { PageLayout } from "../components/PageLayout";

const severityStyles = {
  high: "border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300",
  medium: "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300",
  low: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300",
};

function getSeverityMeta(control) {
  const level = String(control.severity || "low").toLowerCase();
  const label = level === "high" ? "High" : level === "medium" ? "Medium" : "Low";
  return {
    level: ["high", "medium", "low"].includes(level) ? level : "low",
    label,
    style: severityStyles[["high", "medium", "low"].includes(level) ? level : "low"],
  };
}

export default function Phase4Scope() {
  const navigate = useNavigate();
  const { auditRun, advancePhase, controls = [] } = useAudit();
  const [selected, setSelected] = useState([]);
  const [depth, setDepth] = useState("Standard");
  const [search, setSearch] = useState("");
  const [severity, setSeverity] = useState("all");
  const [selectedFilter, setSelectedFilter] = useState("all"); // all | selected | unselected
  const [activeArea, setActiveArea] = useState(null); // null = All Controls
  const [reviewOpen, setReviewOpen] = useState(false);

  useEffect(() => {
    if (!controls || controls.length === 0) return;
    const allControlIds = controls.map((c) => c.control_id);
    const savedScope = auditRun?.scope;

    if (savedScope?.selected_controls) {
      const validSelected = savedScope.selected_controls.filter((cid) =>
        allControlIds.includes(cid)
      );
      if (
        validSelected.length === 0 ||
        validSelected.length !== savedScope.selected_controls.length
      ) {
        setSelected(allControlIds);
      } else {
        setSelected(validSelected);
      }
      setDepth(savedScope.depth_level || "Standard");
    } else {
      setSelected(allControlIds);
    }
  }, [controls, auditRun?.scope]);

  const toggleControl = (cid) => {
    setSelected((prev) =>
      prev.includes(cid) ? prev.filter((c) => c !== cid) : [...prev, cid]
    );
  };

  const handleSave = async () => {
    try {
      await API.put(`/audit-runs/${auditRun.id}/scope`, {
        selected_controls: selected,
        depth_level: depth,
      });
      await advancePhase(5);
      navigate("/phase/5");
      toast.success("Scope defined. Proceeding to Artifact Collection.");
    } catch (e) {
      toast.error("Failed to save scope");
    }
  };

  const totalCount = controls.length;
  const highRiskCount = useMemo(
    () =>
      controls.filter((c) => String(c.severity || "").toLowerCase() === "high")
        .length,
    [controls]
  );

  const areas = useMemo(
    () => [...new Set(controls.map((c) => c.mapped_process_area || "Unassigned"))],
    [controls]
  );

  const areaCounts = useMemo(() => {
    const counts = {};
    controls.forEach((c) => {
      const area = c.mapped_process_area || "Unassigned";
      counts[area] = (counts[area] || 0) + 1;
    });
    return counts;
  }, [controls]);

  const filteredControls = useMemo(() => {
    const q = search.trim().toLowerCase();
    return controls.filter((c) => {
      const matchesSearch =
        !q ||
        [c.control_id, c.description, c.mapped_process_area].some((value) =>
          String(value || "").toLowerCase().includes(q)
        );
      const matchesSeverity =
        severity === "all" || String(c.severity || "").toLowerCase() === severity;
      const matchesSelected =
        selectedFilter === "all" ||
        (selectedFilter === "selected" && selected.includes(c.control_id)) ||
        (selectedFilter === "unselected" && !selected.includes(c.control_id));
      const matchesArea = !activeArea || c.mapped_process_area === activeArea;
      return (
        matchesSearch && matchesSeverity && matchesSelected && matchesArea
      );
    });
  }, [controls, search, severity, selectedFilter, activeArea, selected]);

  const groupedControls = useMemo(() => {
    return filteredControls.reduce((groups, control) => {
      const area = control.mapped_process_area || "Unassigned";
      if (!groups[area]) groups[area] = [];
      groups[area].push(control);
      return groups;
    }, {});
  }, [filteredControls]);

  const clearSelection = () => setSelected([]);

  const selectedControlsList = useMemo(
    () => controls.filter((c) => selected.includes(c.control_id)),
    [controls, selected]
  );

  return (
    <PageLayout
      icon={Target}
      title="Phase 4 – Audit Scope Definition"
      subtitle="Select the controls that will be tested."
      actions={
        <>
          <Badge variant="outline" className="h-8 px-4 font-mono text-xs hidden md:flex">
            {selected.length} / {totalCount} Selected
          </Badge>
          <Button
            onClick={handleSave}
            disabled={selected.length === 0}
            className="shadow-lg shadow-primary/20"
          >
            Save &amp; Continue <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </>
      }
      data-testid="phase-4"
    >
      <div className="flex h-full flex-col gap-4">
        {/* Filter bar */}
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <div className="relative min-w-[240px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search controls..."
              className="h-9 pl-9 pr-3 text-xs"
            />
          </div>

          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            className="h-9 rounded-md border bg-background px-3 text-xs"
            aria-label="Filter by risk level"
          >
            <option value="all">All Risk Levels</option>
            <option value="high">High Risk</option>
            <option value="medium">Medium Risk</option>
            <option value="low">Low Risk</option>
          </select>

          <select
            value={selectedFilter}
            onChange={(e) => setSelectedFilter(e.target.value)}
            className="h-9 rounded-md border bg-background px-3 text-xs"
            aria-label="Filter by selection status"
          >
            <option value="all">All</option>
            <option value="selected">Selected</option>
            <option value="unselected">Unselected</option>
          </select>

          <Button
            variant="outline"
            size="sm"
            className="h-9 gap-1.5 text-xs"
            title="More filters"
          >
            <ListFilter className="h-3.5 w-3.5" />
            More filters
          </Button>
        </div>

        {/* Two-pane body */}
        <div className="grid min-h-0 flex-1 grid-cols-[230px_minmax(0,1fr)] gap-4 overflow-hidden">
          {/* Control areas sidebar */}
          <aside className="flex min-h-0 flex-col overflow-hidden rounded-xl border bg-card">
            <div className="shrink-0 border-b px-3 py-2.5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Control Areas
              </p>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-2">
              <button
                type="button"
                onClick={() => setActiveArea(null)}
                className={`flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors ${
                  !activeArea
                    ? "bg-primary/10 font-semibold text-primary"
                    : "text-foreground hover:bg-muted/40"
                }`}
              >
                <span>All Controls</span>
                <Badge variant="secondary" className="text-[10px] font-mono">
                  {totalCount}
                </Badge>
              </button>

              <div className="my-1.5 border-t border-border/50" />

              {areas.map((area) => (
                <button
                  key={area}
                  type="button"
                  onClick={() =>
                    setActiveArea((current) => (current === area ? null : area))
                  }
                  className={`flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors ${
                    activeArea === area
                      ? "bg-primary/10 font-semibold text-primary"
                      : "text-foreground hover:bg-muted/40"
                  }`}
                >
                  <span className="min-w-0 truncate">{area}</span>
                  <Badge variant="secondary" className="text-[10px] font-mono">
                    {areaCounts[area] || 0}
                  </Badge>
                </button>
              ))}
            </div>

            <div className="shrink-0 space-y-1.5 border-t px-3 py-2.5 text-xs">
              <div className="flex items-center gap-2 text-emerald-600">
                <CheckCircle2 className="h-3.5 w-3.5" />
                <span className="font-medium">{selected.length} selected</span>
              </div>
              <div className="flex items-center gap-2 text-red-600">
                <Shield className="h-3.5 w-3.5" />
                <span className="font-medium">{highRiskCount} high risk</span>
              </div>
            </div>
          </aside>

          {/* Controls list */}
          <section className="min-h-0 overflow-y-auto rounded-xl border bg-card">
            {Object.keys(groupedControls).length === 0 ? (
              <div className="flex min-h-[320px] flex-col items-center justify-center text-center">
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                  <Search className="h-5 w-5 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium">No matching controls</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Try changing your search or filters.
                </p>
                <Button
                  variant="link"
                  size="sm"
                  className="mt-2 text-xs"
                  onClick={() => {
                    setSearch("");
                    setSeverity("all");
                    setSelectedFilter("all");
                    setActiveArea(null);
                  }}
                >
                  Clear all filters
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-border/50">
                {Object.entries(groupedControls).map(([area, areaControls]) => {
                  const areaSelectedCount = areaControls.filter((c) =>
                    selected.includes(c.control_id)
                  ).length;
                  const allAreaSelected = areaSelectedCount === areaControls.length;

                  return (
                    <div key={area}>
                      <div className="flex items-center gap-3 border-b bg-muted/20 px-4 py-2.5">
                        <span className="text-sm font-semibold">{area}</span>
                        <Badge variant="secondary" className="text-[10px] font-mono">
                          {areaSelectedCount} / {areaControls.length}
                        </Badge>
                        <button
                          type="button"
                          onClick={() => {
                            if (allAreaSelected) {
                              setSelected((prev) =>
                                prev.filter(
                                  (id) =>
                                    !areaControls.map((c) => c.control_id).includes(id)
                                )
                              );
                            } else {
                              setSelected((prev) => [
                                ...new Set([
                                  ...prev,
                                  ...areaControls.map((c) => c.control_id),
                                ]),
                              ]);
                            }
                          }}
                          className="ml-auto text-xs font-medium text-primary hover:underline"
                        >
                          {allAreaSelected ? "Deselect all" : `Select all ${areaControls.length}`}
                        </button>
                      </div>

                      <div className="divide-y divide-border/40">
                        {areaControls.map((control) => {
                          const meta = getSeverityMeta(control);
                          const isSelected = selected.includes(control.control_id);
                          return (
                            <div
                              key={control.id}
                              className={`flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors ${
                                isSelected ? "bg-primary/[0.03]" : "hover:bg-muted/30"
                              }`}
                              onClick={() => toggleControl(control.control_id)}
                            >
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => toggleControl(control.control_id)}
                                className="mt-0.5"
                                onClick={(e) => e.stopPropagation()}
                                aria-label={`Select ${control.control_id}`}
                              />
                              <div className="flex min-w-0 flex-1 flex-col gap-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-[11px] font-bold uppercase tracking-tight text-primary/80">
                                    {control.control_id}
                                  </span>
                                  <Badge variant="outline" className={`gap-1 text-[10px] font-medium ${meta.style}`}>
                                    <span
                                      className={`h-1.5 w-1.5 rounded-full ${
                                        meta.level === "high"
                                          ? "bg-red-500"
                                          : meta.level === "medium"
                                            ? "bg-amber-500"
                                            : "bg-emerald-500"
                                      }`}
                                    />
                                    {meta.label}
                                  </Badge>
                                </div>
                                <span
                                  className={`text-[13px] leading-tight ${
                                    isSelected ? "font-medium text-foreground" : "text-muted-foreground"
                                  }`}
                                >
                                  {control.description}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        {/* Bottom action bar */}
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 rounded-xl border bg-card px-4 py-3">
          <span className="text-sm font-medium">
            <span className="font-mono font-bold">{selected.length}</span>{" "}
            controls selected
          </span>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-9 text-xs"
              onClick={clearSelection}
              disabled={selected.length === 0}
            >
              Clear
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-9 gap-1.5 text-xs"
              onClick={() => setReviewOpen(true)}
              disabled={selected.length === 0}
            >
              <Eye className="h-3.5 w-3.5" />
              Review Selection
            </Button>
            <Button
              size="sm"
              className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-9 px-4 shadow-md shadow-emerald-600/30 active:scale-95 border-0"
              onClick={handleSave}
              disabled={selected.length === 0}
              data-testid="phase-4-continue-btn"
            >
              Save &amp; Continue <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Review selection dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Selection</DialogTitle>
            <DialogDescription>
              {selectedControlsList.length} control(s) will be included in this
              audit's scope.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto px-6">
            {selectedControlsList.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No controls selected yet.
              </p>
            ) : (
              <div className="space-y-1.5">
                {selectedControlsList.map((control) => {
                  const meta = getSeverityMeta(control);
                  return (
                    <div
                      key={control.id}
                      className="flex items-start gap-3 rounded-lg border bg-muted/20 p-2.5"
                    >
                      <span className="mt-0.5 shrink-0 font-mono text-[10px] font-bold uppercase text-primary/80">
                        {control.control_id}
                      </span>
                      <span className="min-w-0 flex-1 text-xs leading-5">
                        {control.description}
                      </span>
                      <Badge
                        variant="outline"
                        className={`shrink-0 gap-1 text-[10px] font-medium ${meta.style}`}
                      >
                        {meta.label}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(false)}>
              Close
            </Button>
            <Button onClick={() => { setReviewOpen(false); handleSave(); }}>
              Save &amp; Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
